/**
 * Document 2 · Phase 9.3 — Rep AI Usage self-service tile.
 *
 * Read-only Dashboard tile showing today's AI call count vs. cap for each
 * surface the current rep uses. Amber tone at 80% cap, red at 100%.
 *
 * Consumes GET /api/ai-usage/my-summary. No admin scope.
 */
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Sparkles, RefreshCw, Loader2 } from 'lucide-react';
import { useApi } from '../../hooks/useApi';


function tone(pct) {
  if (pct >= 100) return { ring: 'border-red-300 bg-red-50', bar: 'bg-red-500', text: 'text-red-800' };
  if (pct >= 80) return { ring: 'border-amber-300 bg-amber-50', bar: 'bg-amber-500', text: 'text-amber-800' };
  return { ring: 'border-emerald-200 bg-emerald-50/60', bar: 'bg-emerald-500', text: 'text-emerald-800' };
}


export default function MyAIUsageTile() {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      const res = await api.get('/ai-usage/my-summary');
      setData(res);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load usage');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Card className="rounded-sm border" data-testid="my-ai-usage-tile">
        <CardContent className="p-5 flex items-center justify-center min-h-[120px]">
          <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const surfaces = (data && Array.isArray(data.surfaces)) ? data.surfaces : [];

  return (
    <Card className="rounded-sm border" data-testid="my-ai-usage-tile">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#B89B5E]" />
            <h3 className="text-sm font-semibold tracking-wide uppercase">My AI Usage · Today</h3>
          </div>
          <button
            type="button"
            onClick={() => { setRefreshing(true); load(); }}
            className="text-muted-foreground hover:text-foreground transition"
            aria-label="Refresh"
            data-testid="my-ai-usage-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {err && (
          <div className="text-xs rounded-sm border border-red-200 bg-red-50 text-red-700 px-2 py-1.5 mb-3" data-testid="my-ai-usage-error">
            {err}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {surfaces.map((s) => {
            const pct = s.cap > 0 ? Math.min(100, Math.round((s.used_today / s.cap) * 100)) : 0;
            const t = tone(pct);
            return (
              <div
                key={s.surface}
                className={`rounded-sm border px-3 py-2.5 ${t.ring}`}
                data-testid={`my-ai-usage-${s.surface}`}
              >
                <div className="flex items-baseline justify-between gap-2 mb-1.5">
                  <span className={`text-xs font-medium ${t.text}`}>{s.label}</span>
                  <span className="text-[11px] text-muted-foreground font-mono">
                    {s.used_today}/{s.cap}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-white/60 overflow-hidden">
                  <div
                    className={`h-full ${t.bar} transition-all duration-300`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {surfaces.some((s) => s.used_today >= s.cap) && (
          <div className="mt-3">
            <Badge variant="outline" className="text-[10px] border-red-300 text-red-700 bg-red-50 rounded-sm">
              Daily cap reached on one or more surfaces
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
